import React, { useState, useMemo } from 'react';
import { Modal, Tabs, Tab, Form, Button } from 'react-bootstrap';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import * as SimpleIcons from 'simple-icons';

// Import specific IconPark icons
import {
    BankCardTwo, WalletTwo, ChartHistogram, TrendTwo, Calculator, Bill, Funds, Shopping,
    HomeTwo, HospitalTwo, Book, GameTwo, SettingTwo, PhoneTwo, LinkTwo,
    EditTwo, DeleteTwo, PayCode, ScanCode, Transaction, Exchange,
    Help, Lock, Unlock, Download, Upload, Share, MoreOne,
} from '@icon-park/react';

const iconParkComponents = {
    BankCardTwo, WalletTwo, ChartHistogram, TrendTwo, Calculator, Bill, Funds, Shopping,
    HomeTwo, HospitalTwo, Book, GameTwo, SettingTwo, PhoneTwo, LinkTwo, EditTwo, DeleteTwo, PayCode, ScanCode,
    Transaction, Exchange, Help, Lock, Unlock, Download, Upload, Share, MoreOne,
};

// Filter out undefined components
const validIconParkComponents = Object.fromEntries(
    Object.entries(iconParkComponents).filter(([key, value]) => value !== undefined)
);

const iconParkNames = Object.keys(validIconParkComponents);

// Simple Icons for brands https://simpleicons.org
const popularBrands = [
    'amazon', 'costco', 'chase', 'walmart', 'target', 'starbucks', 'mcdonalds', 'apple', 'google', 'microsoft',
    'netflix', 'spotify', 'uber', 'lyft', 'airbnb', 'paypal', 'visa', 'mastercard', 'americanexpress',
    'bankofamerica', 'wellsfargo', 'citibank', 'capitalone', 'discover', 'tesla', 'nike', 'adidas',
    'facebook', 'instagram', 'twitter', 'linkedin', 'youtube', 'tiktok', 'snapchat', 'whatsapp', 'bmw',
    'robinhood', 'puma', 'applepay', 'meta', 'hdfcbank', 'mahindra', 'toyota', 'tata', 'hyundai', 'honda',
    'icicibank', 'googlepay'
];

const brandIcons = popularBrands.filter(brand => SimpleIcons[`si${brand.charAt(0).toUpperCase() + brand.slice(1)}`]);

const renderIconParkIcon = (iconName, size = 24, theme = "multi-color", fill = ['#333', '#2F88FF', '#FFF', '#43CCF8']) => {
    const IconComponent = validIconParkComponents[iconName];
    if (IconComponent) {
        return <IconComponent theme={theme} size={size} fill={fill} />;
    }
    return null;
};

const renderBrandIcon = (brandName, size = 24) => {
    const iconKey = `si${brandName.charAt(0).toUpperCase() + brandName.slice(1)}`;
    const iconData = SimpleIcons[iconKey];
    if (iconData) {
        return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill={`#${iconData.hex}`}>
                <path d={iconData.path} />
            </svg>
        );
    }
    return null;
};

const IconPicker = ({ show, handleClose, onSelectIcon }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('emojis');

    const handleEmojiSelect = (emoji) => {
        onSelectIcon({ type: 'emoji', value: emoji.native });
        handleClose();
    };

    const handleIconParkClick = (iconName) => {
        onSelectIcon({ type: 'iconpark', value: iconName, theme: 'multi-color', fill: ['#333', '#2F88FF', '#FFF', '#43CCF8'] });
        handleClose();
    };

    const handleBrandIconClick = (brandName) => {
        const iconKey = `si${brandName.charAt(0).toUpperCase() + brandName.slice(1)}`;
        const iconData = SimpleIcons[iconKey];
        onSelectIcon({ type: 'brand', value: brandName, hex: iconData.hex, path: iconData.path });
        handleClose();
    };

    const filteredIconParkNames = useMemo(() => {
        if (!searchTerm) {
            return iconParkNames;
        }
        return iconParkNames.filter(name =>
            name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm]);

    const filteredBrandIcons = useMemo(() => {
        if (!searchTerm) {
            return brandIcons;
        }
        return brandIcons.filter(name =>
            name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm]);

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Select an Icon</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                    <Tab eventKey="emojis" title="Emojis">
                        <Picker data={data} onEmojiSelect={handleEmojiSelect} />
                    </Tab>
                    <Tab eventKey="icons" title="Icons">
                        <Form.Group className="mb-3">
                            <Form.Control
                                type="text"
                                placeholder="Search for an icon..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </Form.Group>
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', 
                            gap: '10px', 
                            maxHeight: '400px', 
                            overflowY: 'auto' 
                        }}>
                            {filteredIconParkNames.map((iconName) => (
                                <Button
                                    key={iconName}
                                    variant="outline-secondary"
                                    onClick={() => handleIconParkClick(iconName)}
                                    className="p-3 d-flex flex-column align-items-center justify-content-center"
                                    style={{ height: '100px' }}
                                    title={iconName}
                                >
                                    {renderIconParkIcon(iconName)}
                                    <span className="mt-2" style={{ fontSize: '12px', wordBreak: 'break-word' }}>
                                        {iconName}
                                    </span>
                                </Button>
                            ))}
                        </div>
                    </Tab>
                    <Tab eventKey="brands" title="Brands">
                        <Form.Group className="mb-3">
                            <Form.Control
                                type="text"
                                placeholder="Search for a brand..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </Form.Group>
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', 
                            gap: '10px', 
                            maxHeight: '400px', 
                            overflowY: 'auto' 
                        }}>
                            {filteredBrandIcons.map((brandName) => (
                                <Button
                                    key={brandName}
                                    variant="outline-secondary"
                                    onClick={() => handleBrandIconClick(brandName)}
                                    className="p-3 d-flex flex-column align-items-center justify-content-center"
                                    style={{ height: '100px' }}
                                    title={brandName}
                                >
                                    {renderBrandIcon(brandName)}
                                    <span className="mt-2" style={{ fontSize: '12px', wordBreak: 'break-word', textTransform: 'capitalize' }}>
                                        {brandName}
                                    </span>
                                </Button>
                            ))}
                        </div>
                    </Tab>
                </Tabs>
            </Modal.Body>
        </Modal>
    );
};

export default IconPicker;


